import html2pdf from 'html2pdf.js/dist/html2pdf' // Full path needed to properly mock

import {
  filenameFriendlyDate,
  getElementByXPath,
  getMaxCanvasArea,
  getPrintableArea,
  getScaleToRatio,
  getXPathForElement,
  mmToPx,
  pxToMm,
} from './utils'
import { PAGE_SIZES } from './constants'

// An amount of pixels to trim off of images to ensure no white space is left over.
// Errors due to rounding and bugs in html2pdf.js
const TRIM_FIX = -10

const MINIMUM_SCALE = 0.01
const LOGGING = false

// Style overrides
function applyKeepStyles({ clonedDocument, keepStyles }) {
  Object.values(keepStyles ?? {})?.forEach((callback) => callback({ clonedDocument }))
}

// html2pdf.js (>= 0.14) renders a *clone* of the source inside a `.html2pdf__container`
// that it appends to the page, then html2canvas clones the whole document again for
// `onclone`. The source therefore appears twice in the cloned document, so the xpath
// (which assumes html2canvas prunes the clone to a single path, as older versions did)
// no longer points at the element that actually gets rendered. Prefer the cloned source
// inside the container; fall back to the xpath for environments without the container
// (e.g. unit tests using the html2pdf mock).
function getClonedElement({ clonedDocument, clonedElementXpath }) {
  const container = clonedDocument.querySelector?.('.html2pdf__container')
  return container?.firstElementChild ?? getElementByXPath(clonedElementXpath, clonedDocument)
}

// For single page PDFs/images we can center the element by scaling
function getCenterAttributes({ clonedElement, pageOrientation, pageSize }) {
  // Determine the scale after keepStyles methods have run.
  const scaleToRatio = getScaleToRatio({
    elementHeight: clonedElement.scrollHeight,
    elementWidth: clonedElement.scrollWidth,
    pageOrientation,
    pageSize,
  })

  let pageWidthPx
  let pageHeightPx
  if (Array.isArray(pageSize)) {
    ;[pageWidthPx, pageHeightPx] = pageSize
  } else {
    const printableArea = getPrintableArea({ pageOrientation, pageSize })
    pageHeightPx = printableArea.pageHeightPx
    pageWidthPx = printableArea.pageWidthPx
  }

  const scaleToHeight = Math.floor(pageHeightPx * scaleToRatio)
  const scaleToWidth = Math.floor(pageWidthPx * scaleToRatio)

  const top = Math.ceil((scaleToHeight - clonedElement.scrollHeight * scaleToRatio) / 2)
  const insetInlineStart = Math.ceil((scaleToWidth - clonedElement.scrollWidth * scaleToRatio) / 2)

  return { scaleToRatio, scaleToHeight, scaleToWidth, top, insetInlineStart }
}

// Resolves a page/image size — a named PAGE_SIZES key or a [width, height] px pair — to its
// long and short edges in millimeters.
function getEdgesMm(size) {
  if (Array.isArray(size)) {
    return { longEdge: pxToMm(Math.max(...size)), shortEdge: pxToMm(Math.min(...size)) }
  }
  return PAGE_SIZES[size]
}

// jsPDF's `format` wants the size in millimeters for custom (array) sizes and the named key
// otherwise. https://raw.githack.com/MrRio/jsPDF/master/docs/jsPDF.html
function getJsPdfFormat({ fileType, imageSize, imageSizeMm, pageSize, pageSizeMm }) {
  if (fileType === 'pdf') {
    return Array.isArray(pageSize) ? pageSizeMm : pageSize
  }
  return Array.isArray(imageSize) ? imageSizeMm : imageSize
}

// Resolves the max canvas dimensions, falling back to the browser's limits when unspecified.
async function getMaxCanvas(maxCanvasSize) {
  if (maxCanvasSize === undefined && maxCanvasSize !== null) {
    return getMaxCanvasArea()
  }
  return { height: maxCanvasSize, width: maxCanvasSize }
}

// Builds the base PDF page margins (mm). Centered output uses uniform margins on every edge.
// Otherwise the top/bottom are skewed to work around a Safari bug that drops the top margin
// (https://github.com/eKoopmans/html2pdf.js/issues/590), and the left (feed-direction) edge is
// doubled because many printers can't print near it, which would otherwise clip content.
function getPdfMargin({ isWebkit, printMargin, shouldCenter }) {
  if (shouldCenter) {
    const centeredMargin = printMargin * 2
    return [centeredMargin, centeredMargin, centeredMargin, centeredMargin]
  }

  const topFactor = isWebkit ? 2 : 1
  const bottomFactor = isWebkit ? 1 : 2
  return [printMargin * topFactor, printMargin, printMargin * bottomFactor, printMargin * 2]
}

// Orders a size's long/short edges into [width, height] mm for the given orientation.
function orientEdgesMm({ longEdge, shortEdge }, pageOrientation) {
  return pageOrientation === 'portrait' ? [longEdge, shortEdge] : [shortEdge, longEdge]
}

// Builds a `.catch` handler that logs the worker error and, outside of tests, reports it as an
// Error carrying the original cause. `report` decides how it surfaces: pass a Promise's `reject`,
// or `rethrow` to throw it (rejecting the awaiting caller).
function handleWorkerError(message, report) {
  return (error) => {
    if (LOGGING) {
      console.error(error)
    }

    if (process.env.NODE_ENV !== 'test') {
      report(new Error(message, { cause: error }))
    }
  }
}

function rethrow(error) {
  throw error
}

// Caps `scale` so the rendered canvas never exceeds the browser's max canvas dimensions.
function capScale(scale, clonedElement, maxCanvasHeight, maxCanvasWidth) {
  const height = Number.parseFloat(clonedElement.style.height)
  const width = Number.parseFloat(clonedElement.style.width)
  const maxScale = Math.min(maxCanvasHeight / Math.ceil(height), maxCanvasWidth / Math.ceil(width))
  return Math.min(scale, maxScale)
}

// Centers `clonedElement` on the page by scaling it down and pushing it in with page margins.
// html2canvas captures the html2pdf container, not the element itself; the element is scaled down
// (top-left origin) to fit the page, so a full-size container would be captured with the content
// in one corner and the rest blank. Shrinking the container to the scaled element makes the
// captured canvas the content, which the returned margins then center on the page.
// Returns the margins (mm) to apply to the html2pdf options.
function centerByMargin({ clonedElement, container, heightPx, pageOrientation, printMargin, size, widthPx }) {
  const { scaleToRatio } = getCenterAttributes({ clonedElement, pageOrientation, pageSize: size })

  const marginTop = Math.ceil((heightPx - clonedElement.scrollHeight * scaleToRatio) / 2)
  const marginInlineStart = Math.ceil((widthPx - clonedElement.scrollWidth * scaleToRatio) / 2)

  const naturalHeight = clonedElement.scrollHeight
  const naturalWidth = clonedElement.scrollWidth
  clonedElement.style.height = `${naturalHeight}px`
  clonedElement.style.transform = `scale(${scaleToRatio})`
  clonedElement.style.transformOrigin = 'top left' // Top left needed to properly show content
  clonedElement.style.width = `${naturalWidth}px`

  if (container) {
    container.style.height = `${Math.ceil(naturalHeight * scaleToRatio)}px`
    container.style.width = `${Math.ceil(naturalWidth * scaleToRatio)}px`
  }

  return [printMargin + pxToMm(marginTop), printMargin + pxToMm(marginInlineStart), printMargin, printMargin]
}

// Centers a single-page PDF inside `.html2pdf__container`. html2pdf renders the source into the
// container and scales THAT canvas to the page width, paginating by height. To land a single,
// centered, page-filling result, size the container to the page's printable area (mirroring
// html2pdf's own margin-derived `inner` box), scale the content to fit inside it, and center it
// there — rather than shifting the whole canvas with the PDF margin — so every edge keeps equal
// space and nothing is clipped when printing.
function centerInContainer({ clonedElement, container, margin, pageOrientation, sizePage }) {
  const marginHeightMm = margin[0] + margin[2]
  const marginWidthMm = margin[1] + margin[3]
  const fullWidthMm = pageOrientation === 'portrait' ? sizePage.shortEdge : sizePage.longEdge
  const fullHeightMm = pageOrientation === 'portrait' ? sizePage.longEdge : sizePage.shortEdge
  const innerWidthPx = Math.floor(mmToPx(fullWidthMm - marginWidthMm))
  // Shave a couple pixels so rounding never pushes the canvas past a single page. Otherwise
  // html2pdf adds a sliver of a second page, which is later trimmed away and clips the bottom.
  const innerHeightPx = Math.floor(mmToPx(fullHeightMm - marginHeightMm)) - 2

  const scaleToRatio = Math.min(innerWidthPx / clonedElement.scrollWidth, innerHeightPx / clonedElement.scrollHeight)
  const scaledHeight = clonedElement.scrollHeight * scaleToRatio
  const scaledWidth = clonedElement.scrollWidth * scaleToRatio

  clonedElement.style.height = `${clonedElement.scrollHeight}px`
  clonedElement.style.width = `${clonedElement.scrollWidth}px`
  clonedElement.style.transform = `scale(${scaleToRatio})`
  clonedElement.style.transformOrigin = 'top left' // Top left needed to properly show content
  clonedElement.style.marginBlockStart = `${Math.max(0, Math.floor((innerHeightPx - scaledHeight) / 2))}px`
  clonedElement.style.marginInlineStart = `${Math.max(0, Math.floor((innerWidthPx - scaledWidth) / 2))}px`

  container.style.height = `${innerHeightPx}px`
  container.style.overflow = 'hidden'
  container.style.width = `${innerWidthPx}px`
}

// Force LTR direction for print to prevent RTL language settings from causing blank print previews.
function forceLtrDirection(clonedDocument) {
  if (clonedDocument.documentElement) {
    clonedDocument.documentElement.style.direction = 'ltr'
  }
  if (clonedDocument.body) {
    clonedDocument.body.style.direction = 'ltr'
  }
}

// Works around an html2canvas bug in its manual DOM renderer (the mode custom-font designs use — see
// `foreignObjectRendering` below): a `font-family` name containing a colon, such as the app's brand
// body font `HeritageBody:Sans`, breaks html2canvas's font parsing, so the text both falls back to a
// system font and has its words mis-spaced. In a custom-font design the copyright footer is the only
// text still using the brand font (name boxes use their own, colon-free, font), so pin it to a
// web-safe family for the render. Only needed in manual mode; foreignObject mode renders it correctly.
function fixColonFontForManualRender(clonedDocument) {
  const style = clonedDocument.createElement('style')
  style.textContent =
    '[data-testid="copyright-footer"], [data-testid="copyright-footer"] * { font-family: Arial, Helvetica, sans-serif !important; }'
  ;(clonedDocument.head ?? clonedDocument.body ?? clonedDocument.documentElement)?.appendChild(style)
}

// Copies the keep font-face <style> tags into the clone so custom fonts render in the output.
function injectKeepFonts(clonedDocument) {
  const styles = document.querySelectorAll('style[data-id="keep-font-face"]')
  styles.forEach((style) => {
    const xpath = getXPathForElement(style.parentElement).replace(/\[\d+\]/g, '[1]')
    const clonedElement = getElementByXPath(xpath, clonedDocument)

    if (clonedElement) {
      clonedElement.appendChild(style.cloneNode(true))
    }
  })
}

// html2pdf.js wraps the source in a `position: absolute; margin: auto` container before rendering.
// That combination makes html2canvas's foreignObjectRendering produce a blank canvas, so the
// auto-centering (which only affects the invisible wrapper, not our content) is neutralized here.
// The source is also sized relative to its on-screen containing block and laid out with absolute
// positioning, while the container has a fixed page width and `height: auto` — so the clone
// collapses to zero height and content spills outside the narrow page width. Pinning the container
// to the source's real rendered box keeps the clone matching what is shown on screen.
// Returns the container element, or null when absent (e.g. the unit-test mock).
function neutralizeHtml2pdfContainer(clonedDocument, source) {
  const html2pdfContainer = clonedDocument.querySelector?.('.html2pdf__container')
  if (!html2pdfContainer) {
    return null
  }

  html2pdfContainer.style.margin = '0'

  const sourceHeight = source?.offsetHeight
  const sourceWidth = source?.offsetWidth
  if (sourceHeight) {
    html2pdfContainer.style.height = `${sourceHeight}px`
  }
  if (sourceWidth) {
    html2pdfContainer.style.width = `${sourceWidth}px`
  }

  return html2pdfContainer
}

// Remove helper/admin mode role indicator styling that can cause cropped output.
// --role-indicator-color is set by @fs/zion-role-indicator when active.
function stripRoleIndicator(clonedDocument) {
  const roleIndicatorWrapper = clonedDocument.querySelector?.('[style*="--role-indicator-color"]')
  if (roleIndicatorWrapper) {
    roleIndicatorWrapper.style['--role-indicator-border'] = '0px'
    roleIndicatorWrapper.style.border = 'none'
    roleIndicatorWrapper.style.margin = '0px'
  }
}

// Renders `element` to a raster image, re-centers it on a white canvas, and resolves with the
// image data, blob, filename, and a worker whose `save` opens and downloads the blob.
function generateImage({
  element,
  fileType,
  finalFilename,
  html2PdfOptions,
  imageHeightPx,
  imageQuality,
  imageSize,
  imageWidthPx,
  scale,
}) {
  return new Promise((resolve, reject) => {
    const worker = new html2pdf.Worker()
    worker
      .set(html2PdfOptions)
      .from(element)
      .toImg()
      .get('img')
      .then(async (image) => {
        if (!image || !image.src || image.src === 'data:,') {
          reject(new Error('Unable to generate image'))
        }

        // Wait for the image to fully load.
        await new Promise((imageLoadedResolve) => {
          image.onload = () => {
            requestAnimationFrame(() => {
              imageLoadedResolve()
            })
          }
        })

        let canvasWidthPx
        let canvasHeightPx
        if (Array.isArray(imageSize)) {
          ;[canvasWidthPx, canvasHeightPx] = imageSize
        } else {
          canvasWidthPx = imageWidthPx
          canvasHeightPx = imageHeightPx
        }

        const scaleHeight = canvasHeightPx * scale
        const scaleWidth = canvasWidthPx * scale
        const dh = image.height
        const dw = image.width
        const dxDiff = (scaleWidth - dw) / 2
        const dyDiff = (scaleHeight - dh) / 2
        const dx = dxDiff > 0 ? dxDiff : 0
        const dy = dyDiff > 0 ? dyDiff : 0
        const canvas = document.createElement('canvas')
        canvas.height = scaleHeight - dx + TRIM_FIX
        canvas.width = scaleWidth - dy + TRIM_FIX
        const context = canvas.getContext('2d')
        context.fillStyle = '#FFFFFF'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, TRIM_FIX, TRIM_FIX, scaleWidth, scaleHeight)
        const finalImageBlob = await new Promise((resolveBlob) => {
          canvas.toBlob(resolveBlob, `image/${fileType}`, imageQuality)
        })

        const originalSave = worker.save
        worker.save = (download) => {
          return new Promise((downloadResolve) => {
            const blobUrl = URL.createObjectURL(finalImageBlob)

            // Open in new tab with security settings
            window.open(blobUrl, '_blank', 'noopener,noreferrer')

            // Also trigger download
            const link = document.createElement('a')
            link.setAttribute('download', download ?? finalFilename)
            link.setAttribute('href', blobUrl)
            link.setAttribute('style', 'display: none;')
            link.setAttribute('type', `image/${fileType}`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Clean up blob URL after tab/download has time to start
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100)

            worker.save = originalSave

            downloadResolve()
          })
        }

        resolve({
          image: canvas.toDataURL(`image/${fileType}`, imageQuality),
          imageBlob: finalImageBlob,
          filename: finalFilename,
          worker,
        })
      })
  })
}

// Renders `fromFirst` (and any further `pages`) into a multi-page-capable PDF and resolves with
// { pdf, worker }. `onPageProcessing` is notified as each page is added.
function generatePdf({ finalFilename, fromFirst, html2PdfOptions, onPageProcessing, pages, totalPages }) {
  if (totalPages > 1) {
    onPageProcessing?.(1, totalPages)
  }

  return new Promise((resolve, reject) => {
    let worker = new html2pdf.Worker()
      .set(html2PdfOptions)
      .from(fromFirst)
      .toPdf()
      .catch(handleWorkerError('Unable to generate PDF', reject))

    // https://github.com/eKoopmans/html2pdf.js/issues/19#issuecomment-455868274
    if (totalPages > 1) {
      for (let i = 1; i < totalPages; i++) {
        worker = worker
          .set(html2PdfOptions)
          .get('pdf')
          .then((pdf) => {
            onPageProcessing?.(i + 1, totalPages)
            // Don't add a page if we already have a blank page
            if (pdf.getNumberOfPages() < i + 1) {
              pdf.addPage()
            }
          })
          .from(pages[i])
          .toCanvas()
          .toPdf()
          .catch(handleWorkerError('Unable to generate PDF', reject))
      }
    }

    worker
      .set(html2PdfOptions)
      .get('pdf')
      .then((pdf) => {
        if (!pdf) {
          reject(new Error('Unable to generate PDF'))
        }

        const interval = setInterval(() => {
          if (pdf?.setProperties) {
            clearInterval(interval)
            // Title is used by some browsers as the filename if opened from the
            // print dialog window.
            pdf.setProperties({ title: finalFilename.replace('.pdf', '') })
            if (totalPages) {
              while (totalPages < pdf.getNumberOfPages()) {
                pdf.deletePage(pdf.getNumberOfPages())
              }
            }
            resolve({ pdf, worker })
          }
        }, 100)
      })
      .catch(handleWorkerError('Unable to generate PDF', reject))
  })
}

// html2canvas computes the captured element's window-relative bounds from the LIVE document
// before any cloning happens (`onclone`, and thus `forceLtrDirection` above, runs too late to
// affect this). When the live document is `dir="rtl"`, that computation goes wrong in a way that
// silently offsets the render (a blank or partially-shifted canvas, no error): confirmed by
// forcing the live `dir` to `ltr` around the capture, which corrects the offset. Restored
// afterward so the visible page's RTL layout doesn't otherwise change.
export default async function htmlToOutput(options) {
  const originalDocumentDir = document.documentElement.dir
  if (originalDocumentDir !== 'rtl') {
    return htmlToOutputInner(options)
  }

  document.documentElement.dir = 'ltr'
  try {
    return await htmlToOutputInner(options)
  } finally {
    document.documentElement.dir = originalDocumentDir
  }
}

async function htmlToOutputInner({
  dpi = 600,
  element,
  elementXpath,
  fileType = 'pdf',
  filename = `file-${filenameFriendlyDate()}`,
  imageQuality = 1,
  imageScale = 2, // Similar to DPI but for images
  imageSize = 'a4Square',
  keepStyles = {},
  maxCanvasSize,
  onPageProcessing = () => {},
  pageSelector = ':scope > *',
  pageOrientation = 'portrait',
  pageSize = 'a4',
  processFonts = false,
  shouldCenter = false, // Center `element` on the page for single page PDFs/images only.
  totalPages,
  useManualFontRendering = false,
}) {
  // When elements are cloned only one child is cloned until the selected element.
  // Replacing here ensures that we select the correct element.
  const clonedElementXpath = elementXpath.replace(/\[\d+\]/g, '[1]')

  const sizePage = getEdgesMm(pageSize)
  const pageSizeMm = orientEdgesMm(sizePage, pageOrientation)

  const { pageHeightPx, pageWidthPx, printMargin } = getPrintableArea({ pageOrientation, pageSize })
  if (pageSize === 'a4Square') {
    pageSize = [pageWidthPx, pageHeightPx]
  }

  const sizeImage = getEdgesMm(imageSize)
  const imageSizeMm = orientEdgesMm(sizeImage, pageOrientation)

  const { pageHeightPx: imageHeightPx, pageWidthPx: imageWidthPx } = getPrintableArea({
    pageOrientation,
    pageSize: imageSize,
  })
  if (imageSize === 'a4Square') {
    imageSize = [imageWidthPx, imageHeightPx]
  }

  const { height: maxCanvasHeight, width: maxCanvasWidth } = await getMaxCanvas(maxCanvasSize)

  const finalFilename = `${filename.replace(/[^\w-]+/g, '_')}.${fileType.replace('jpeg', 'jpg')}`
  // PDFs are just images added to a PDF
  // When choosing between jpeg and png, jpeg is significantly faster (more performant) than png (noticeable particularly in Safari).
  const imageType = fileType === 'pdf' ? 'jpeg' : fileType

  let onclone = () => {}
  // https://html2canvas.hertzen.com/configuration
  const html2canvasOptions = {
    // html2canvas renders in one of two mutually exclusive modes. Its foreignObject mode (serialize
    // to an SVG and rasterize it as an image) faithfully reproduces SVG content — curved name boxes
    // (`CurvedNameBox`) and the pedigree trees' connector lines, box shadows, and text spacing — but
    // the SVG-image sandbox does NOT load `@font-face` fonts, so text silently falls back to a
    // generic system font (on Chrome the first print/PDF renders the fallback; a later attempt may
    // happen to render correctly once the font is warm). Its manual DOM mode (foreignObject off)
    // renders custom fonts correctly on the first try, but drops SVG `<textPath>` and mangles other
    // SVG rendering. Only designs that register their own custom fonts need manual mode, so keying
    // the mode off `useManualFontRendering` (not `processFonts`, which is truthy for every design
    // because of the always-present base fonts) keeps all SVG-based designs on foreignObject.
    foreignObjectRendering: !useManualFontRendering, // Whether to render foreignObjects in SVGs.
    logging: LOGGING, // Whether to log messages to the console
    onclone,
    removeContainer: true, // Removes the iFrame used to clone the element
    // The scale to use for rendering. Defaults to the browsers device pixel ratio.
    // The option {scale: 1} is equivalent to 96 dpi * devicePixelRatio, and larger values of will increase the resolution.
    // https://github.com/niklasvh/html2canvas/pull/1087
    scale: MINIMUM_SCALE,
  }

  const jsPDFOptions = {
    compress: true,
    format: getJsPdfFormat({ fileType, imageSize, imageSizeMm, pageSize, pageSizeMm }),
    orientation: pageOrientation,
  }

  // https://stackoverflow.com/a/70585394/2344083
  const isWebkit = 'GestureEvent' in window
  // https://stackoverflow.com/a/52855084/2344083
  const isTouchDevice = window.matchMedia('(pointer: coarse)').matches

  // https://ekoopmans.github.io/html2pdf.js/#options
  const html2PdfOptions = {
    filename: finalFilename,
    html2canvas: html2canvasOptions,
    image: { type: imageType, quality: imageQuality },
    jsPDF: jsPDFOptions,
    margin: fileType === 'pdf' ? getPdfMargin({ isWebkit, printMargin, shouldCenter }) : [0, 0, 0, 0],
  }

  // The element html2pdf renders from (a single page for multi-page output, otherwise the
  // whole element). Declared here so `onclone` can measure its rendered size.
  const pages = totalPages > 1 ? element.querySelectorAll(pageSelector) : null
  const fromFirst = totalPages > 1 ? pages?.[0] : element

  let scale = fileType === 'pdf' ? dpi / 96 : imageScale
  onclone = (clonedDocument) => {
    const html2pdfContainer = neutralizeHtml2pdfContainer(clonedDocument, fromFirst)

    if (processFonts) {
      injectKeepFonts(clonedDocument)
    }

    if (useManualFontRendering) {
      fixColonFontForManualRender(clonedDocument)
    }

    applyKeepStyles({ clonedDocument, keepStyles })
    forceLtrDirection(clonedDocument)
    stripRoleIndicator(clonedDocument)

    const clonedElement = getClonedElement({ clonedDocument, clonedElementXpath })

    // The centering helpers below read `clonedElement.scrollWidth/scrollHeight` as the content's
    // natural size. `neutralizeHtml2pdfContainer` pins the surrounding container to the source's
    // on-screen box, but the element itself still collapses to the html2pdf page width in the
    // clone (it has no intrinsic width — on screen its width comes from the ZoomPan stage, which is
    // absent in the clone). When it collapses, fixed-pixel content (e.g. an artistic design's
    // `--image-width`-sized background and absolutely-positioned name boxes) keeps its full width
    // and overflows the narrow box, centered, on BOTH sides. `scrollWidth` only counts the
    // rightward overflow, so it under-measures the true content width — the scale comes out too
    // large and the sides get clipped. Pinning the element to its real rendered size keeps the
    // clone matching the screen. Only needed for the centering paths; the non-centered image path
    // captures the full (correctly sized) container regardless.
    if (shouldCenter) {
      if (fromFirst?.offsetWidth) {
        clonedElement.style.width = `${fromFirst.offsetWidth}px`
      }
      if (fromFirst?.offsetHeight) {
        clonedElement.style.height = `${fromFirst.offsetHeight}px`
      }
    }

    if (shouldCenter && fileType === 'pdf' && !Array.isArray(pageSize) && html2pdfContainer) {
      centerInContainer({
        clonedElement,
        container: html2pdfContainer,
        margin: html2PdfOptions.margin,
        pageOrientation,
        sizePage,
      })
    } else if (shouldCenter) {
      html2PdfOptions.margin = centerByMargin({
        clonedElement,
        container: html2pdfContainer,
        heightPx: fileType === 'pdf' ? pageHeightPx : imageHeightPx,
        pageOrientation,
        printMargin,
        size: fileType === 'pdf' ? pageSize : imageSize,
        widthPx: fileType === 'pdf' ? pageWidthPx : imageWidthPx,
      })
    }

    scale = capScale(scale, clonedElement, maxCanvasHeight, maxCanvasWidth)

    html2PdfOptions.html2canvas = { ...html2canvasOptions, scale }
  }
  html2PdfOptions.html2canvas.onclone = onclone

  // Retrieves the correct scale and margins that we want to render at
  // by running the onclone method.
  await new html2pdf.Worker()
    .set(html2PdfOptions)
    .from(fromFirst)
    .toPdf()
    .catch(handleWorkerError('Unable to generate Output', rethrow))

  // There is an issue on webkit browsers where the background image is not shown on
  // the first render of that image; possibly due to the image being a progressive JPEG.
  // See: https://stackoverflow.com/a/17667926/2344083, https://stackoverflow.com/a/17668013/2344083
  // However, even after converting the image to a baseline JPEG or other format, the issue
  // still occurs.
  // We can work around this by on desktop by running toPdf twice.
  if (isWebkit) {
    await new html2pdf.Worker().set(html2PdfOptions).from(fromFirst).toPdf()

    // Or three times in the case of iOS 🤦
    if (isTouchDevice) {
      await new html2pdf.Worker().set(html2PdfOptions).from(fromFirst).toPdf()
    }
  }

  // JPEG
  if (fileType !== 'pdf') {
    return generateImage({
      element,
      fileType,
      finalFilename,
      html2PdfOptions,
      imageHeightPx,
      imageQuality,
      imageSize,
      imageWidthPx,
      scale,
    })
  }

  // PDF
  return generatePdf({ finalFilename, fromFirst, html2PdfOptions, onPageProcessing, pages, totalPages })
}
